import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import InputBox from '../../atomic/inputbox/InputBox';
import { MenuProps, sharedInputSx } from '../Products/kitchenProductsConstants';
import { teamSignupBranchAPI } from '../../context-api/api-services/TeamSignupBranch.service';
import { ALL_ROUTES, BLANK_FORM, fieldSxMultiline } from './branchTeamsConfig';
import {
  buildBlankRouteAccess,
  formatTeamsErrors,
  avatarInitialSx,
  getInitials,
} from './branchTeamsUtils';
import { Transition, RouteAccessRow } from './BranchTeamsShared';

/* ── design tokens (no purple, matches listing page) ── */
const M = {
  orange: '#fc5d1b',
  navy: '#181436',
  pageBg: '#F8FAFC',
  cardBg: '#FFFFFF',
  border: '#E2E8F0',
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  sectionBg: '#F8FAFC',
  shadowOrange: '0 8px 28px rgba(252,93,27,0.30)',
  shadow: '0 24px 64px rgba(15,23,42,0.14), 0 4px 16px rgba(15,23,42,0.08)',
};

/* ── SectionHeading ── */
function SectionHeading({ icon: Icon, label, description, step }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: `${M.orange}12`,
          border: `1px solid ${M.orange}28`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: 0.15,
        }}
      >
        <Icon sx={{ fontSize: 15, color: M.orange }} />
      </Box>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {step && (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: M.navy,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.6rem',
                fontFamily: 'Inter Bold',
                flexShrink: 0,
              }}
            >
              {step}
            </Box>
          )}
          <Typography sx={{ fontFamily: 'Inter Bold', fontSize: '0.84rem', color: M.textPrimary }}>
            {label}
          </Typography>
        </Box>
        {description && (
          <Typography
            sx={{ fontSize: '0.72rem', color: M.textMuted, mt: 0.2, fontFamily: 'Inter Regular' }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** Same `InputBox` / `FormControl` styling as `KitchenProducts.js` (`sharedInputSx`). */
const kitchenField = { width: '100%', height: 'auto', ...sharedInputSx };
const kitchenFieldMultiline = { width: '100%', height: 'auto', ...fieldSxMultiline };
const selectFormControlSx = {
  ...sharedInputSx,
  width: '100%',
  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
};

export function AddMemberModal({ open, onClose, onSuccess, branchName }) {
  const [form, setForm] = useState(BLANK_FORM);
  const [routeAccess, setRouteAccess] = useState(buildBlankRouteAccess());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm({ ...BLANK_FORM });
      setRouteAccess(buildBlankRouteAccess());
      setSaveError(null);
      setFieldErrors({});
    }
  }, [open]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setFieldErrors((fe) => ({ ...fe, [key]: undefined }));
  };

  function validateForm() {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!form.email.trim()) errs.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = 'Invalid email';
    if (!form.phone_no.trim()) errs.phone_no = 'Required';
    else if (!/^\d{10}$/.test(form.phone_no.trim())) errs.phone_no = 'Must be exactly 10 digits';
    if (!form.password.trim()) errs.password = 'Required';
    else if (form.password.trim().length < 5) errs.password = 'Min 5 characters';
    if (!form.team_user_id.trim()) errs.team_user_id = 'Required';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    setSaveError(null);

    const activeRoutes = routeAccess.filter((r) => r.is_active);
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone_no: form.phone_no.trim(),
      password: form.password.trim(),
      branchName: branchName || '',
      team_user_id: form.team_user_id.trim(),
      user_type: form.user_type,
      dedicatedRole: form.dedicatedRole,
      manual_notes: form.manual_notes.trim(),
      routeAccess: activeRoutes,
    };

    const res = await teamSignupBranchAPI(payload);
    if (res.hasError) {
      setSaveError(res.error?.message || 'Request failed. Please try again.');
      setSaving(false);
      return;
    }
    const body = res.data;
    if (body?.success === false) {
      setSaveError(formatTeamsErrors(body.errors) || body.CustomMessage || 'Signup failed.');
      setSaving(false);
      return;
    }
    setSaving(false);
    onSuccess?.();
    onClose();
  };

  const allActive = routeAccess.every((r) => r.is_active);
  const toggleAllRoutes = () =>
    setRouteAccess((prev) => prev.map((r) => ({ ...r, is_active: !allActive })));
  const updateRoute = (idx, updated) =>
    setRouteAccess((prev) => prev.map((r, i) => (i === idx ? updated : r)));
  const activeCount = routeAccess.filter((r) => r.is_active).length;

  const fullName = [form.firstName, form.lastName].filter(Boolean).join(' ') || 'New';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: {
          borderRadius: '10px',
          boxShadow: M.shadow,
          overflow: 'hidden',
          maxHeight: '94vh',
          border: `1px solid ${M.border}`,
          bgcolor: M.pageBg,
        },
      }}
    >
      {/* ── MODAL HEADER ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2.25,
          bgcolor: M.cardBg,
          borderBottom: `1px solid ${M.border}`,
          flexShrink: 0,
        }}
      >
        {/* orange icon */}
        {/* <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '5px',
            background: `linear-gradient(135deg, ${M.orange} 0%, #ff7c47 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // boxShadow: '0 6px 20px rgba(252,93,27,0.35)',
            flexShrink: 0,
          }}
        >
          <PersonAddAltOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box> */}

        {/* title block */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter Bold',
              fontSize: '1.05rem',
              color: M.textPrimary,
              lineHeight: 1.2,
            }}
          >
            Add Team Member
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.3, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '12px', color: M.textMuted, fontFamily: 'Inter Regular' }}>
              Roster for
            </Typography>
            {branchName && (
              <Box
                sx={{
                  borderRadius: '3px',
                  color: 'gray',
                  fontSize: '12px',
                  fontFamily: 'Inter Bold',
                  letterSpacing: '0.5px',
                }}
              >
                {branchName}
              </Box>
            )}
            <Typography sx={{ fontSize: '12px', color: M.textMuted, fontFamily: 'Inter Regular' }}>
              · Fill all fields and configure page access
            </Typography>
          </Box>
        </Box>

        {/* close button */}
        <IconButton
          onClick={onClose}
          size='small'
          sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            border: `1.5px solid ${M.border}`,
            bgcolor: M.cardBg,
            color: M.textMuted,
            flexShrink: 0,
            '&:hover': { bgcolor: M.navy, borderColor: M.navy, color: '#fff' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* ── SCROLLABLE CONTENT ── */}
      <DialogContent sx={{ p: 0, overflowY: 'auto', bgcolor: M.pageBg }}>
        {/* error alert */}
        {saveError && (
          <Box sx={{ px: 3, pt: 2.5 }}>
            <Alert
              severity='error'
              sx={{
                borderRadius: '10px',
                fontFamily: 'Inter Regular',
                fontSize: '0.83rem',
                bgcolor: '#FFF1F2',
                border: '1px solid #fca5a5',
              }}
            >
              {saveError}
            </Alert>
          </Box>
        )}

        {/* ── AVATAR PREVIEW STRIP ── */}
        <Box
          sx={{
            mx: 3,
            mt: 2.5,
            p: 2,
            bgcolor: M.cardBg,
            borderRadius: '12px',
            border: `1px solid ${M.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar
            sx={avatarInitialSx({
              width: 56,
              height: 56,
              fontSize: '1.2rem',
              borderRadius: '14px',
              flexShrink: 0,
            })}
          >
            {getInitials(form.firstName, form.lastName)}
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Inter Bold',
                fontSize: '0.95rem',
                color: M.textPrimary,
                lineHeight: 1.2,
              }}
            >
              {fullName === 'New' ? 'New Member' : fullName}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: M.textMuted,
                fontFamily: 'Inter Regular',
                mt: 0.25,
              }}
            >
              {form.email || 'Email not set'}{' '}
              {form.user_type && `· ${form.user_type.replace('_', ' ')}`}
            </Typography>
          </Box>
          {branchName && (
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1.25,
                py: 0.6,
                borderRadius: '8px',
                bgcolor: M.pageBg,
                border: `1px solid ${M.border}`,
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e' }} />
              <Typography
                sx={{ fontSize: '0.7rem', fontFamily: 'Inter Bold', color: M.textSecondary }}
              >
                {branchName}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── SECTION 1: PERSONAL INFO ── */}
        <Box sx={{ px: 3, pt: 3 }}>
          <SectionHeading
            icon={BadgeOutlinedIcon}
            step='1'
            label='Personal Details'
            description='Name, email, phone and login credentials'
          />

          <Box
            sx={{
              p: 2.5,
              bgcolor: M.cardBg,
              borderRadius: '12px',
              border: `1px solid ${M.border}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* name row */}
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
            >
              <InputBox
                label='First Name'
                name='firstName'
                value={form.firstName}
                onChange={set('firstName')}
                error={!!fieldErrors.firstName}
                inputErrorMsg={fieldErrors.firstName || ''}
                width='100%'
                height='auto'
                sx={kitchenField}
              />
              <InputBox
                label='Last Name'
                name='lastName'
                value={form.lastName}
                onChange={set('lastName')}
                error={!!fieldErrors.lastName}
                inputErrorMsg={fieldErrors.lastName || ''}
                width='100%'
                height='auto'
                sx={kitchenField}
              />
            </Box>

            {/* email */}
            <InputBox
              label='Email Address'
              type='email'
              name='email'
              value={form.email}
              onChange={set('email')}
              error={!!fieldErrors.email}
              inputErrorMsg={fieldErrors.email || ''}
              width='100%'
              height='auto'
              sx={kitchenField}
            />

            {/* phone + password */}
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
            >
              <InputBox
                label='Phone (10 digits)'
                name='phone_no'
                value={form.phone_no}
                onChange={set('phone_no')}
                error={!!fieldErrors.phone_no}
                inputErrorMsg={fieldErrors.phone_no || ''}
                width='100%'
                height='auto'
                sx={kitchenField}
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
              />
              <InputBox
                label='Password (min 5 chars)'
                type='password'
                name='password'
                value={form.password}
                onChange={set('password')}
                error={!!fieldErrors.password}
                inputErrorMsg={fieldErrors.password || ''}
                width='100%'
                height='auto'
                sx={kitchenField}
              />
            </Box>
          </Box>
        </Box>

        {/* ── SECTION 2: IDENTITY ── */}
        <Box sx={{ px: 3, pt: 3 }}>
          <SectionHeading
            icon={LockOutlinedIcon}
            step='2'
            label='Identity & ID'
            description='Unique team user ID for this member within the branch'
          />
          <Box
            sx={{
              p: 2.5,
              bgcolor: M.cardBg,
              borderRadius: '12px',
              border: `1px solid ${M.border}`,
            }}
          >
            <InputBox
              label='Team User ID'
              name='team_user_id'
              value={form.team_user_id}
              onChange={set('team_user_id')}
              error={!!fieldErrors.team_user_id}
              inputErrorMsg={fieldErrors.team_user_id || 'Must be unique within this branch'}
              width='100%'
              height='auto'
              sx={kitchenField}
            />
          </Box>
        </Box>

        {/* ── SECTION 3: ROLE ── */}
        <Box sx={{ px: 3, pt: 3 }}>
          <SectionHeading
            icon={ManageAccountsOutlinedIcon}
            step='3'
            label='Role & Type'
            description="Define the member's user type and dedicated role in the branch"
          />
          <Box
            sx={{
              p: 2.5,
              bgcolor: M.cardBg,
              borderRadius: '12px',
              border: `1px solid ${M.border}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
            >
              <FormControl size='small' sx={selectFormControlSx}>
                <InputLabel id='add-user-type-label'>User Type</InputLabel>
                <Select
                  labelId='add-user-type-label'
                  value={form.user_type}
                  label='User Type'
                  onChange={set('user_type')}
                  input={<OutlinedInput label='User Type' />}
                  MenuProps={MenuProps}
                >
                  {[
                    { value: 'team_member', label: 'Team Member' },
                    { value: 'regional_branch', label: 'Regional Branch' },
                  ].map((o) => (
                    <MenuItem
                      key={o.value}
                      value={o.value}
                      sx={{ fontFamily: 'Inter Regular', fontSize: '0.85rem' }}
                    >
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size='small' sx={selectFormControlSx}>
                <InputLabel id='add-dedicated-role-label'>Dedicated Role</InputLabel>
                <Select
                  labelId='add-dedicated-role-label'
                  value={form.dedicatedRole}
                  label='Dedicated Role'
                  onChange={set('dedicatedRole')}
                  input={<OutlinedInput label='Dedicated Role' />}
                  MenuProps={MenuProps}
                >
                  {['marketing', 'sales', 'hr', 'superadmin', 'none'].map((r) => (
                    <MenuItem
                      key={r}
                      value={r}
                      sx={{
                        fontFamily: 'Inter Regular',
                        fontSize: '0.85rem',
                        textTransform: 'capitalize',
                      }}
                    >
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        {/* ── SECTION 4: NOTES ── */}
        <Box sx={{ px: 3, pt: 3 }}>
          <SectionHeading
            icon={NoteAltOutlinedIcon}
            step='4'
            label='Notes'
            description='Optional internal notes about this team member'
          />
          <Box
            sx={{
              p: 2.5,
              bgcolor: M.cardBg,
              borderRadius: '12px',
              border: `1px solid ${M.border}`,
            }}
          >
            <InputBox
              label='Manual Notes (optional)'
              name='manual_notes'
              value={form.manual_notes}
              onChange={set('manual_notes')}
              width='100%'
              height='auto'
              sx={kitchenFieldMultiline}
              multiline
              minRows={2}
              maxRows={4}
            />
          </Box>
        </Box>

        {/* ── SECTION 5: PAGE ACCESS ── */}
        <Box sx={{ px: 3, pt: 3, pb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              mb: 2,
            }}
          >
            <SectionHeading
              icon={RouteOutlinedIcon}
              step='5'
              label='Page Access'
              description={`${activeCount} of ${ALL_ROUTES.length} pages enabled — toggle routes and set permissions`}
            />
            <Button
              size='small'
              onClick={toggleAllRoutes}
              sx={{
                fontFamily: 'Inter Bold',
                fontSize: '0.72rem',
                color: allActive ? '#ef4444' : M.orange,
                textTransform: 'none',
                borderRadius: '8px',
                border: `1px solid ${allActive ? '#fca5a5' : `${M.orange}30`}`,
                px: 1.5,
                py: 0.4,
                bgcolor: allActive ? '#FFF1F2' : `${M.orange}08`,
                flexShrink: 0,
                mt: 0.25,
                '&:hover': {
                  bgcolor: allActive ? '#fee2e2' : `${M.orange}15`,
                },
              }}
            >
              {allActive ? 'Disable All' : 'Enable All'}
            </Button>
          </Box>

          <Box
            sx={{
              bgcolor: M.cardBg,
              borderRadius: '12px',
              border: `1px solid ${M.border}`,
              overflow: 'hidden',
            }}
          >
            {ALL_ROUTES.map((r, idx) => (
              <Box key={r.route}>
                <Box sx={{ px: 2, py: 0.75 }}>
                  <RouteAccessRow
                    entry={routeAccess[idx]}
                    label={r.label}
                    onChange={(updated) => updateRoute(idx, updated)}
                  />
                </Box>
                {idx < ALL_ROUTES.length - 1 && <Divider sx={{ borderColor: M.border, mx: 2 }} />}
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>

      {/* ── FOOTER ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          px: 3,
          py: 2,
          bgcolor: M.cardBg,
          borderTop: `1px solid ${M.border}`,
          flexShrink: 0,
        }}
      >
        {/* summary pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.75rem', color: M.textMuted, fontFamily: 'Inter Regular' }}>
            {activeCount > 0 ? (
              <>
                <Box component='span' sx={{ fontFamily: 'Inter Bold', color: M.textPrimary }}>
                  {activeCount}
                </Box>
                {` page${activeCount !== 1 ? 's' : ''} enabled`}
              </>
            ) : (
              'No pages enabled yet'
            )}
          </Typography>
        </Box>

        {/* actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={onClose}
            variant='outlined'
            sx={{
              borderColor: M.border,
              color: M.textSecondary,
              fontFamily: 'Inter Bold',
              borderRadius: '10px',
              textTransform: 'none',
              px: 2.5,
              fontSize: '0.83rem',
              '&:hover': { borderColor: M.navy, bgcolor: `${M.navy}08`, color: M.navy },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant='contained'
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={14} sx={{ color: '#fff' }} />
              ) : (
                <CheckCircleRoundedIcon sx={{ fontSize: 17 }} />
              )
            }
            sx={{
              background: `linear-gradient(135deg, ${M.orange} 0%, #ff7c47 100%)`,
              color: '#fff',
              fontFamily: 'Inter Bold',
              borderRadius: '10px',
              textTransform: 'none',
              px: 3,
              fontSize: '0.83rem',
              boxShadow: M.shadowOrange,
              '& .MuiButton-startIcon .MuiSvgIcon-root': { color: '#fff' },
              '&:hover': {
                background: `linear-gradient(135deg, #e84e10 0%, ${M.orange} 100%)`,
                boxShadow: '0 12px 32px rgba(252,93,27,0.45)',
              },
              '&.Mui-disabled': { opacity: 0.55 },
            }}
          >
            {saving ? 'Adding Member…' : 'Add Member'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
